/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { FormulaElementOptionImpl } from "abreact/expression/domain/ElementOptionImpl";
import { ValueCategoryAndTypeImpl } from "abreact/expression/domain/ValueCategoryAndTypeImpl";
import { Expression } from "abreact/expression/Expression";
import { GuidedExpressionEditor } from "abreact/expression/ui/editor/GuidedExpressionEditor";
import { AbstractLeafOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/AbstractLeafOperandValueImpl";
import { LiteralOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/LiteralOperandValueImpl";
import { OperatorOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/OperatorOperandValueImpl";
import { ExpArgumentOperandLabelPlacementEnum } from "abweb/generated/abwebinternal/api/dto/ExpArgumentOperand";
import { Assert } from "abweb/utils/Assert";
import { Lambdas } from "abweb/utils/lambda/Lambdas";
import { LocallyUniqueIdSet } from "abweb/utils/LocallyUniqueIdSet";
import * as Immutable from "immutable";
export let GuidedExpressionEditorUtils;
(function (_GuidedExpressionEditorUtils) {
  var OperatorOperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue;
  const DEFAULT_ID_PREFIX = "ClientGeneratedId-";

  /*
   * Loop over the input options and find the one for which there is a value within the row state.
   */
  function getArgumentOperandValue(argumentOperandValuesByOperandName, argumentOperand, forSummary = false) {
    let operandValue;
    for (let elementInfo of argumentOperand.elementInfos) {
      const {
        name: operandName
      } = elementInfo.expArgumentOperand;
      if (argumentOperandValuesByOperandName.has(operandName)) {
        operandValue = argumentOperandValuesByOperandName.get(operandName);
        break;
      }
    }
    if (operandValue != null && forSummary) {
      if (AbstractLeafOperandValueImpl.isInstance(operandValue) && operandValue.valueTypeIDs.includes(Expression.Domain.Constants.VALUE_TYPE_BOOLEAN) && operandValue.value === String(false)) {
        operandValue = undefined;
      }
    }
    return operandValue;
  }
  _GuidedExpressionEditorUtils.getArgumentOperandValue = getArgumentOperandValue;
  function getArgumentOperands(expressionDomainModel, sourceOperandValue, expOperator, selectAdvanced = false) {
    return sourceOperandValue != null ? getArgumentOperandsWithOverloading(expressionDomainModel, sourceOperandValue, expOperator, selectAdvanced) : getArgumentOperandsWithoutOverloading(expressionDomainModel, expOperator, selectAdvanced);
  }
  _GuidedExpressionEditorUtils.getArgumentOperands = getArgumentOperands;
  function domainContainsCategoryWithID(expExpressionDomain, categoryID) {
    let categoryFound = false;
    if (expExpressionDomain.categories) {
      for (let cat of expExpressionDomain.categories) {
        if (cat.id === categoryID) {
          categoryFound = true;
          break;
        }
      }
    }
    return categoryFound;
  }
  _GuidedExpressionEditorUtils.domainContainsCategoryWithID = domainContainsCategoryWithID;
  function defaultFormulaElementOptionCreator(expArgumentOperand, valueCategoryID, valueTypeID, multiple) {
    let formulaOption = undefined;
    if (!expArgumentOperand.noFormulaInput && valueCategoryID === Expression.Domain.Constants.CATEGORY_LITERAL && valueTypeID === Expression.Domain.Constants.VALUE_TYPE_NUMBER && !multiple) {
      formulaOption = new FormulaElementOptionImpl(this.getExpValueCategory(Expression.Domain.Constants.CATEGORY_FORMULA), this.getExpValueType(valueTypeID), multiple);
    }
    return formulaOption;
  }
  _GuidedExpressionEditorUtils.defaultFormulaElementOptionCreator = defaultFormulaElementOptionCreator;
  function getArgumentOperandsWithOverloading(expressionDomainModel, sourceOperandValue, expOperator, selectAdvanced = false) {
    return expressionDomainModel.computeArgumentOperands(expOperator.id, sourceOperandValue.valueCategoryID, sourceOperandValue.valueTypeIDs, sourceOperandValue.multiple, Immutable.List.of(Expression.Domain.Constants.ROLE_FUNCTION), selectAdvanced);
  }
  _GuidedExpressionEditorUtils.getArgumentOperandsWithOverloading = getArgumentOperandsWithOverloading;
  function getArgumentOperandsWithoutOverloading(expressionDomainModel, expOperator, selectAdvanced = false) {
    return expressionDomainModel.computeArgumentOperandsNoOverloading(expOperator.id, Immutable.List.of(Expression.Domain.Constants.ROLE_FUNCTION), selectAdvanced);
  }
  _GuidedExpressionEditorUtils.getArgumentOperandsWithoutOverloading = getArgumentOperandsWithoutOverloading;
  function createGenerateIdFunction(expExpressionDefinition, idPrefix) {
    idPrefix ??= DEFAULT_ID_PREFIX;
    /**
     * Once initialized, use {@link locallyUniqueIdSet} to ensure every {@link ExpExpressionNode#id} in
     * {@link expExpressionDefinition} is populated, and populated with a unique value.
     */
    const expExpressionNodesNeedingGeneratedId = [];
    const locallyUniqueIdSet = initialize(expExpressionDefinition, expExpressionNodesNeedingGeneratedId);
    const generateId = () => {
      return idPrefix + locallyUniqueIdSet.generateId();
    };
    for (const expExpressionNode of expExpressionNodesNeedingGeneratedId) {
      expExpressionNode.id = generateId();
    }
    return generateId;
  }
  _GuidedExpressionEditorUtils.createGenerateIdFunction = createGenerateIdFunction;
  /**
   * Ensure that existing ids are unique among {@link ExpExpressionNode}s that specify them, then initialize a
   * {@link LocallyUniqueIdSet} with awareness of these existing ids.
   */
  function initialize(expExpressionDefinition, expExpressionNodesNeedingGeneratedId) {
    if (expExpressionDefinition == null) {
      return new LocallyUniqueIdSet();
    } else {
      const usedIds = new Set();
      for (const expExpressionNode of GuidedExpressionEditorUtils.collectExpExpressionNodes(expExpressionDefinition)) {
        /**
         * If {@link expExpressionNode} lacks an id or has a duplicate id, collect it here to ensure its id will be,
         * respectively, populated or overwritten with a unique, generated id.
         */
        if (expExpressionNode.id == null || usedIds.has(expExpressionNode.id)) {
          expExpressionNodesNeedingGeneratedId.push(expExpressionNode);
        } else {
          usedIds.add(expExpressionNode.id);
        }
      }
      return new LocallyUniqueIdSet(usedIds);
    }
  }
  function collectOperandValues(operandValue, operandValueCollector) {
    if (operandValue == null) {
      return;
    }
    operandValueCollector.add(operandValue);
    if (OperatorOperandValueImpl.isInstance(operandValue)) {
      collectOperandValues(operandValue.sourceOperandValue, operandValueCollector);
      for (const argumentOperandValue of operandValue.argumentOperandValuesByOperandName.valueSeq().toArray()) {
        collectOperandValues(argumentOperandValue, operandValueCollector);
      }
    }
  }
  _GuidedExpressionEditorUtils.collectOperandValues = collectOperandValues;
  function getFunctionOperatorElementOptionsForValueType(expressionDomainModel, valueTypeID, multiple) {
    return expressionDomainModel.determineApplicableOperatorElementOptionsForOutputType(valueTypeID, multiple, Immutable.List.of(Expression.Domain.Constants.ROLE_FUNCTION));
  }
  _GuidedExpressionEditorUtils.getFunctionOperatorElementOptionsForValueType = getFunctionOperatorElementOptionsForValueType;
  function getIsCompleteOverride(additionalData) {
    const override = additionalData?.values?.[Expression.Domain.Constants.ADDITIONAL_DATA_COMPLETE_OVERRIDE];
    if (override === "true") return true;
    if (override === "false") return false;
    return undefined;
  }

  /**
   * Check if the given operator node is complete, by checking its operands against the domain requirements.
   * It also checks that a source operand is present for condition operators, and no placeholder is used in
   * non-join operators.
   * <p>
   * <strong>Important:</strong>
   * This method expects the operator operand value to have both source and argument operands populated
   * if it is not a JOIN operator.
   */
  function isOperatorComplete(operatorOperandValue) {
    const completeOverride = getIsCompleteOverride(operatorOperandValue.expAdditionalData);
    if (completeOverride != null) {
      return completeOverride;
    }
    if (operatorOperandValue.isPlaceholder) {
      return false;
    }
    if (operatorOperandValue.isCondition && operatorOperandValue.sourceOperandValue == null) {
      return false;
    }
    return isExpOperatorComplete(operatorOperandValue.expOperator, operatorOperandValue.sourceOperandValue, operatorOperandValue.argumentOperandValuesByOperandName);
  }
  _GuidedExpressionEditorUtils.isOperatorComplete = isOperatorComplete;
  function isExpOperatorComplete(expOperator, sourceOperandValue, argumentOperandValuesByOperandName) {
    if (expOperator.roleID === Expression.Domain.Constants.ROLE_JOIN) {
      return isJoinExpOperatorComplete(expOperator, argumentOperandValuesByOperandName);
    }
    return !hasIncompleteOperands(expOperator, sourceOperandValue, argumentOperandValuesByOperandName);
  }
  _GuidedExpressionEditorUtils.isExpOperatorComplete = isExpOperatorComplete;
  function isJoinExpOperatorComplete(expOperator, argumentOperandValuesByOperandName) {
    Assert.assert(expOperator.roleID === Expression.Domain.Constants.ROLE_JOIN);
    return argumentOperandValuesByOperandName.valueSeq().every(argumentOperandValue => {
      const operatorOperandValue = OperatorOperandValueImpl.cast(argumentOperandValue);
      return isOperatorComplete(operatorOperandValue);
    });
  }

  /**
   * Finds the value assigned to an operand group.
   * @throws Error if more than one value found
   */
  function findArgumentOperandValue(argumentOperand, argumentOperandValuesByOperandName) {
    const values = argumentOperand.expArgumentOperands.map(arg => argumentOperandValuesByOperandName.get(arg.name)).filter(Lambdas.isNonNullish);
    Assert.assert(values.length <= 1, "An argument group should have at most 1 value");
    return values.length === 1 ? values[0] : undefined;
  }
  _GuidedExpressionEditorUtils.findArgumentOperandValue = findArgumentOperandValue;
  function hasIncompleteOperands(expOperator, sourceOperandValue, argumentOperandValuesByOperandName) {
    const incompleteExpArgumentOperands = [];
    if (expOperator.sourceOperandTypes?.length ?? 0 > 0) {
      if (sourceOperandValue == null || !isOperandValueComplete(sourceOperandValue)) {
        return true;
      }
    }
    if (expOperator.argumentOperands != null) {
      if (expOperator.argumentOperandGroups != null) {
        for (let argumentOperandGroup of expOperator.argumentOperandGroups) {
          let argumentOperandsInGroup = getExpArgumentOperandsInExpArgumentOperandGroup(expOperator, argumentOperandGroup.id);
          let argumentOperandGroupHasValue = false;
          for (let argumentOperand of argumentOperandsInGroup) {
            if (isExpArgumentOperandComplete(argumentOperand, expOperator, argumentOperandValuesByOperandName)) {
              argumentOperandGroupHasValue = true;
            }
          }
          if (!argumentOperandGroupHasValue) {
            // Note that the operands handle optionality.
            incompleteExpArgumentOperands.push(...argumentOperandsInGroup);
          }
        }
      }
      incompleteExpArgumentOperands.push(...expOperator.argumentOperands.filter(expArgumentOperand => expArgumentOperand.groupID == null).filter(expArgumentOperand => !isExpArgumentOperandComplete(expArgumentOperand, expOperator, argumentOperandValuesByOperandName)));
    }
    return incompleteExpArgumentOperands.length > 0;
  }
  function isExpArgumentOperandComplete(expArgumentOperand, expOperator, argumentOperandValuesByOperandName) {
    if (expOperator.roleID === Expression.Domain.Constants.ROLE_JOIN) {
      return isJoinExpOperatorComplete(expOperator, argumentOperandValuesByOperandName);
    }
    const argumentOperandValue = argumentOperandValuesByOperandName.get(expArgumentOperand.name);
    if (argumentOperandValue) {
      return isOperandValueComplete(argumentOperandValue);
    } else {
      // if no value, then optionality determines whether this is complete or not
      // If in a group, the group's optionality is what counts.
      const group = expOperator.argumentOperandGroups?.find(group => group.id === expArgumentOperand.groupID);
      return group != null ? !!group.optional : !!expArgumentOperand.optional;
    }
  }
  function isOperandValueComplete(operandValue) {
    const completeOverride = getIsCompleteOverride(operandValue.expAdditionalData);
    if (completeOverride != null) {
      return completeOverride;
    }
    if (OperatorOperandValueImpl.isInstance(operandValue)) {
      return isOperatorComplete(operandValue);
    } else if (operandValue.valueCategoryID === Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION) {
      if (LiteralOperandValueImpl.isInstance(operandValue)) {
        const expExpressionDefinitionJson = LiteralOperandValueImpl.getRequiredPropertyValue(operandValue, "stringValue");
        const {
          isComplete
        } = Expression.Definition.parseToMutable(expExpressionDefinitionJson);
        return !!isComplete;
      }
    }
    return operandValue.value != null || operandValue.listValue != null || operandValue.expAdditionalData != null;
  }
  _GuidedExpressionEditorUtils.isOperandValueComplete = isOperandValueComplete;
  function getExpArgumentOperandsInExpArgumentOperandGroup(expOperator, expArgumentOperandGroupID) {
    return expOperator.argumentOperands != null ? expOperator.argumentOperands.filter(expArgumentOperand => expArgumentOperand.groupID === expArgumentOperandGroupID) : [];
  }
  function collectExpExpressionNodes(expExpressionDefinition) {
    const expExpressionNodesSet = new Set();
    collectExpExpressionNodesRecursive(expExpressionDefinition.rootNode, expExpressionNodesSet);
    return Array.from(expExpressionNodesSet);
  }
  _GuidedExpressionEditorUtils.collectExpExpressionNodes = collectExpExpressionNodes;
  function collectExpExpressionNodesRecursive(expExpressionNode, expExpressionNodesSet) {
    expExpressionNodesSet.add(expExpressionNode);
    if (expExpressionNode.operandValues != null) {
      for (const operandValueExpExpressionNode of expExpressionNode.operandValues) {
        collectExpExpressionNodesRecursive(operandValueExpExpressionNode, expExpressionNodesSet);
      }
    }
  }
  _GuidedExpressionEditorUtils.collectExpExpressionNodesRecursive = collectExpExpressionNodesRecursive;
  function determineSummaryValueElementClassName(expArgumentOperandLabelPlacementEnum, both) {
    let base = "ab-guided-expression-editor-summary-element";
    let classNameSuffix;
    if (both) {
      classNameSuffix = "-suffix-and-prefix-label";
    } else {
      switch (expArgumentOperandLabelPlacementEnum) {
        case ExpArgumentOperandLabelPlacementEnum.PREFIX:
          classNameSuffix = "-prefix-label";
          break;
        case ExpArgumentOperandLabelPlacementEnum.SUFFIX:
          classNameSuffix = "-suffix-label";
          break;
        default:
          classNameSuffix = "-no-label";
      }
    }
    return base + classNameSuffix;
  }
  _GuidedExpressionEditorUtils.determineSummaryValueElementClassName = determineSummaryValueElementClassName;
  function retainSourceOperandValue(expOperator, operandValue, expressionDomainModel) {
    if (operandValue == null) {
      return undefined;
    }
    for (const operandType of operandValue.valueTypeIDs.toArray()) {
      const options = expressionDomainModel.determineOperatorApplicability(expOperator, new ValueCategoryAndTypeImpl(operandValue.valueCategoryID, operandType, operandValue.multiple), OperatorOperandValueImpl.isInstance(operandValue)).filter(option => option.isValid);
      if (options.length > 0) {
        return operandValue.withOperandName(expOperator.sourceOperandName);
      }
    }
    return undefined;
  }
  _GuidedExpressionEditorUtils.retainSourceOperandValue = retainSourceOperandValue;
  function retainArgumentOperandValues(expOperator, sourceOperandValue, argumentOperandValuesByOperandName, expressionDomainModel) {
    let retainedArgumentOperandValueByOperandName = new Map();
    const argumentOperands = expressionDomainModel.computeArgumentOperands(expOperator.id, sourceOperandValue.valueCategoryID, sourceOperandValue.valueTypeIDs, sourceOperandValue.multiple, Immutable.List.of(Expression.Domain.Constants.ROLE_FUNCTION));
    for (const argumentOperand of argumentOperands) {
      let exactMatch = false;
      for (const {
        elementOption,
        expArgumentOperand
      } of argumentOperand.elementInfos) {
        const argumentOperandValue = argumentOperandValuesByOperandName.get(expArgumentOperand.name);
        if (argumentOperandValue == null) {
          continue;
        }
        if (!elementOption.isValueOption()) {
          continue;
        }
        const {
          expValueCategory,
          expValueType
        } = elementOption;
        const comparison = new ValueCategoryAndTypeImpl(expValueCategory.id, expValueType.id);
        for (const valueTypeID of argumentOperandValue.valueTypeIDs.toArray()) {
          const existing = new ValueCategoryAndTypeImpl(argumentOperandValue.valueCategoryID, valueTypeID);
          exactMatch = existing.exactMatch(comparison);
          if (exactMatch) {
            retainedArgumentOperandValueByOperandName = retainedArgumentOperandValueByOperandName.set(expArgumentOperand.name, argumentOperandValue);
            break;
          }
        }
        if (exactMatch) {
          break;
        }
      }
    }
    return Immutable.Map(retainedArgumentOperandValueByOperandName);
  }
  _GuidedExpressionEditorUtils.retainArgumentOperandValues = retainArgumentOperandValues;
  function getPrefixLabelIfProvided(expArgumentOperand) {
    if (expArgumentOperand.label && (expArgumentOperand.labelPlacement == null || expArgumentOperand.labelPlacement === "PREFIX")) {
      return expArgumentOperand.label;
    }
  }
  _GuidedExpressionEditorUtils.getPrefixLabelIfProvided = getPrefixLabelIfProvided;
  function getSuffixLabelIfProvided(expArgumentOperand) {
    if (expArgumentOperand.label && expArgumentOperand.labelPlacement === "SUFFIX") {
      return expArgumentOperand.label;
    }
  }
  _GuidedExpressionEditorUtils.getSuffixLabelIfProvided = getSuffixLabelIfProvided;
  function isValueCategoryStale(operandValue, valueCategoryID, expTemplateConfiguration) {
    if (OperatorOperandValueImpl.isInstance(operandValue) && operandValue.isFunction) {
      return false;
    }
    return expTemplateConfiguration == null && operandValue != null && valueCategoryID != null && operandValue.valueCategoryID !== valueCategoryID;
  }
  _GuidedExpressionEditorUtils.isValueCategoryStale = isValueCategoryStale;
  function computeSimpleStructureLines(definitionOrNode,
  // custom creation of the line to grab more info if wanted
  lineFunction) {
    const lines = [];
    let indent = 0;
    computeNodeStructure(lines, definitionOrNode.rootNode ?? definitionOrNode, indent, lineFunction);
    return lines;
  }
  _GuidedExpressionEditorUtils.computeSimpleStructureLines = computeSimpleStructureLines;
  function computeSimpleNodeStructureLines(node, lineFunction) {
    const lines = [];
    let indent = 0;
    computeNodeStructure(lines, node, indent, lineFunction);
    return lines;
  }
  _GuidedExpressionEditorUtils.computeSimpleNodeStructureLines = computeSimpleNodeStructureLines;
  function computeNodeStructure(lines, node, indent, lineFunction) {
    const computeIndent = length => {
      return "--".repeat(length);
    };
    const line = lineFunction != null ? lineFunction(node, indent) : `${computeIndent(indent)} ${node.nodeType} ${node.operandName ?? "unknown"} ${node.operatorID ?? ""} ${node.displayValue ?? ""}`;
    indent = indent + 1;
    lines.push(line);
    node.operandValues?.forEach(op => computeNodeStructure(lines, op, indent, lineFunction));
  }

  /**
   * Determines whether an operand value matches the given category for an operand value editor.
   */
  function operandValueMatchesCategoryForEditor(operandValue, categoryId) {
    if (operandValue == null || categoryId == null) {
      return true;
    }
    // Exception for functions, which use a different category in the serialized form.
    if (categoryId === Expression.Domain.Constants.PSEUDO_CATEGORY_FUNCTION && operandValue.valueCategoryID === Expression.Domain.Constants.CATEGORY_LITERAL) {
      return true;
    }
    return operandValue.valueCategoryID === categoryId;
  }
  _GuidedExpressionEditorUtils.operandValueMatchesCategoryForEditor = operandValueMatchesCategoryForEditor;
  let Dependencies;
  (function (_Dependencies) {
    /**
     * Used to communicate all the arguments and argument groups that have values set
     */

    /**
     * Computes the set of argument operands and groups that have been given values so far.
     * @param expOperator The operator the arguments belong to
     * @param argumentOperandValuesByOperandName The current values for the argument operands
     */
    function computeFilledArgumentOperandsForDependencyCheck(expOperator, argumentOperandValuesByOperandName) {
      const filledArguments = {
        filledArgumentOperandNames: new Set(),
        filledArgumentOperandGroupIds: new Set()
      };
      for (const operand of expOperator.argumentOperands ?? []) {
        const operandValue = argumentOperandValuesByOperandName.get(operand.name);
        if (operandValue != null) {
          filledArguments.filledArgumentOperandNames.add(operand.name);
          if (operand.groupID != null) {
            filledArguments.filledArgumentOperandGroupIds.add(operand.groupID);
          }
        }
      }
      return filledArguments;
    }
    _Dependencies.computeFilledArgumentOperandsForDependencyCheck = computeFilledArgumentOperandsForDependencyCheck;
    function areDependenciesMet(expArgumentOperand, expArgumentOperandGroup, filledArguments) {
      let dependenciesMet = true;
      for (const dep of (expArgumentOperand.dependencies ?? []).concat(expArgumentOperandGroup?.dependencies ?? [])) {
        if (dep.operandName != null) {
          if (!filledArguments.filledArgumentOperandNames.has(dep.operandName)) {
            dependenciesMet = false;
            break;
          }
        } else if (dep.groupID != null) {
          if (!filledArguments.filledArgumentOperandGroupIds.has(dep.groupID)) {
            dependenciesMet = false;
            break;
          }
        }
      }
      return dependenciesMet;
    }
    _Dependencies.areDependenciesMet = areDependenciesMet;
    function isDependentOn(expArgumentOperand, expArgumentOperandGroup, potentialDependency) {
      const dependencies = (expArgumentOperand.dependencies ?? []).concat(expArgumentOperandGroup?.dependencies ?? []);
      const dependencyIndex = dependencies.findIndex(dep => {
        if (dep.groupID != null) {
          return dep.groupID === potentialDependency.groupID;
        }
        if (dep.operandName != null) {
          return dep.operandName === potentialDependency.name;
        }
        return false;
      });
      return dependencyIndex > -1;
    }
    _Dependencies.isDependentOn = isDependentOn;
  })(Dependencies || (Dependencies = _GuidedExpressionEditorUtils.Dependencies || (_GuidedExpressionEditorUtils.Dependencies = {})));
})(GuidedExpressionEditorUtils || (GuidedExpressionEditorUtils = {}));
//# sourceMappingURL=GuidedExpressionEditorUtils.js.map
